import type {
	OrderDetailResponseDto,
	PartnerResponseDto,
} from "Infrastructure/Api/Api";
import { OrderStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { usePartnersListQuery } from "Partner/Api/Query/usePartnersListQuery";
import { transformAndSortPartners } from "Partner/Utilities/transformSortPartners";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { SelectWarningComponent } from "Shared/Components/Form/CustomFormComponent/SelectWarningComponent";
import { Warning } from "@mui/icons-material";
import { Box, FormHelperText, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

type AssignPartnerProps = {
	order: OrderDetailResponseDto;
};

type OrderDurationResponse = {
	jobDurationDays?: number | null;
	effectiveJobDurationDays?: number | null;
};

const formResource = Resources.base.order.editForm;

export function AssignPartnerForm({ order }: AssignPartnerProps) {
	const duration = order as OrderDetailResponseDto & OrderDurationResponse;
	const { region } = order;
	const { t } = useResource();
	const { data: partners } = usePartnersListQuery({
		marketId: order.market?.id,
		verticals: order.verticalId.toString(),
	});

	const {
		control,
		formState: { errors },
		watch,
		setValue,
	} = useFormContext();

	const status = watch("status");
	const jobDurationDays = useWatch({ control, name: "jobDurationDays" });
	const hasManualDuration = jobDurationDays !== "" && jobDurationDays != null;
	const durationValue = hasManualDuration
		? Number(jobDurationDays)
		: duration.effectiveJobDurationDays;
	const effectiveDurationLabel =
		durationValue == null
			? "-"
			: hasManualDuration
				? t(formResource.jobDurationManual, { days: durationValue })
				: t(formResource.jobDurationAuto, { days: durationValue });

	const sortedOptions = useMemo(() => {
		return transformAndSortPartners(
			partners?.data ?? [],
			region?.name,
			"surname-first",
		);
	}, [partners, region]);

	const selectedPartnerId = useWatch({ control, name: "partnerId" });
	const selectedPartner: PartnerResponseDto | undefined = partners?.data?.find(
		({ partner }) => partner.id === Number(selectedPartnerId),
	);

	const isOrderFromDifferentRegion = (): boolean => {
		if (!selectedPartner?.partner?.marketRegionId || !region?.id) {
			return false;
		}

		return selectedPartner.region?.id !== region?.id;
	};

	const handleResetPartner = () => {
		setValue("partnerId", null, { shouldDirty: true });
	};

	return (
		<>
			<Stack alignItems="start">
				<Box width="100%">
					<Select
						id="partnerId"
						fullWidth
						options={sortedOptions}
						variant="outlined"
						name="partnerId"
						control={control}
						errors={errors}
						groupBy="region"
						numeric
						disabled={order.sfInvoicePeriod !== null}
						AdditionalComponent={
							isOrderFromDifferentRegion() ? (
								<SelectWarningComponent
									text={t(formResource.partnerWarning)}
									Icon={Warning}
								/>
							) : null
						}
					/>
				</Box>
				{status !== OrderStatus.done && (
					<Stack
						flexDirection="row"
						justifyContent="space-between"
						width="100%"
						mt={1}
					>
						<Box onClick={handleResetPartner} sx={{ cursor: "pointer" }}>
							<Typography variant="caption" color="primary">
								{t(formResource.reset)}
							</Typography>
						</Box>
					</Stack>
				)}
				<Box width="100%" mt={2}>
					<Input
						id="jobDurationDays"
						name="jobDurationDays"
						label={t(formResource.jobDurationDays)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						placeholder="Auto"
						inputMode="numeric"
						inputChangeRegex={/\D/g}
						inputProps={{ min: 1 }}
					/>
					{!errors.jobDurationDays && (
						<FormHelperText>
							{t(formResource.jobDurationDaysHelper)}
						</FormHelperText>
					)}
				</Box>
				<Box width="100%" mt={2}>
					<Typography variant="caption" color="text.secondary">
						{t(formResource.effectiveJobDurationDays)}
					</Typography>
					<Typography variant="body2">{effectiveDurationLabel}</Typography>
				</Box>
			</Stack>
		</>
	);
}
